// import
import React from "react";
import { Redirect, useLocation } from "react-router-dom";
import Dashboard from "./views/Dashboard/Dashboard";
import Tables from "./views/Dashboard/Tables";
import TradeLog from "./views/Dashboard/TradeLog";
import Journal from "./views/Dashboard/Journal";
import University from "./views/Dashboard/University";
import CourseOverview from "./views/Dashboard/University/CourseOverview";
import CourseLearn from "./views/Dashboard/University/CourseLearn";
import Statistics from "./views/Dashboard/Statistics";
import News from "./views/Dashboard/News";
import BillingSuccess from "./views/Dashboard/Billing/BillingSuccess";
import BillingCanceled from "./views/Dashboard/Billing/BillingCanceled";
import RTLPage from "./views/Dashboard/RTL";
import Profile from "./views/Dashboard/Profile";
import CtraderOAuthCallback from "./views/Dashboard/Profile/components/CtraderOAuthCallback";
import ProfileTabRedirect from "./views/Dashboard/Profile/ProfileTabRedirect";
import ConnectBrokerWizardPage from "./views/Dashboard/Profile/connectBroker/ConnectBrokerWizardPage";
import NotificationsCentre from "./views/Dashboard/NotificationsCentre";
import Admin from "./views/Dashboard/Admin";
import Reports from "./views/Dashboard/Reports";
import ReportDetail from "./views/Dashboard/Reports/ReportDetail";
import GenerateReportPage from "./views/Dashboard/Reports/GenerateReportPage";
import WorkspaceHubPage from "./views/Dashboard/Workspace/WorkspaceHubPage";
import WorkspaceCanvasPage from "./views/Dashboard/Workspace/WorkspaceCanvasPage";
import WorkspaceStrategyCanvasPage from "./views/Dashboard/Workspace/WorkspaceStrategyCanvasPage";
import Strategies from "./views/Dashboard/Strategies/index";
import StrategyDetail from "./views/Dashboard/Strategies/StrategyDetail";
import SimulatorListPage from "./views/Dashboard/ExecutionRulesSimulator/SimulatorListPage";
import SimulatorWizardPage from "./views/Dashboard/ExecutionRulesSimulator/SimulatorWizardPage";
import SimulatorResultsPage from "./views/Dashboard/ExecutionRulesSimulator/SimulatorResultsPage";
import SimulatorSavedRunPage from "./views/Dashboard/ExecutionRulesSimulator/SimulatorSavedRunPage";
import BacktestDetail from "./views/Dashboard/Backtests/BacktestDetail";
import {
  LEGACY_SIMULATOR_BASE_PATH,
  SIMULATOR_LIST_PATH,
} from "./views/Dashboard/ExecutionRulesSimulator/simulatorRoutes";

function BacktestsListRedirect(): JSX.Element {
  return <Redirect to={SIMULATOR_LIST_PATH} />;
}

/** Preserve bookmarks to `/execution-rules-simulator/*`. */
function LegacySimulatorRedirect(): JSX.Element {
  const { pathname, search, hash } = useLocation();
  const suffix = pathname.startsWith(LEGACY_SIMULATOR_BASE_PATH)
    ? pathname.slice(LEGACY_SIMULATOR_BASE_PATH.length)
    : "";
  return <Redirect to={`${SIMULATOR_LIST_PATH}${suffix}${search}${hash}`} />;
}
import SignIn from "./views/Auth/SignIn";
import SignUp from "./views/Auth/SignUp";

import {
  HomeIcon,
  CreditIcon,
  PersonIcon,
  SupportIcon,
  JournalIcon,
  DashboardIcon,
  GlobeIcon,
} from "./components/Icons/Icons";
import { BrainIcon } from "./components/ui/brain";
import { ChartSplineIcon } from "./components/ui/chart-spline";
import { FileChartLineIcon } from "./components/ui/file-chart-line";
import { LayersIcon } from "./components/ui/layers";
import { LayoutPanelTopIcon } from "./components/ui/layout-panel-top-icon";
import { PlayIcon } from "./components/ui/play";
import { GalleryVerticalEndIcon } from "./components/ui/gallery-vertical-end";
import { RadioIcon } from "./components/ui/radio";
import { SettingsMotionIcon } from "./components/ui/settings-motion";
import { RouteConfigArray } from "./types/routes";

const dashRoutes: RouteConfigArray = [
  {
    path: "/signin",
    name: "Sign In",
    rtlName: "تسجيل الدخول",
    component: SignIn,
    layout: "/auth",
  },
  {
    path: "/signup",
    name: "Sign Up",
    rtlName: "إنشاء حساب",
    component: SignUp,
    layout: "/auth",
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    rtlName: "لوحة القيادة",
    icon: <DashboardIcon color="inherit" />,
    component: Dashboard,
    layout: "",
  },
  {
    path: "/paywall",
    name: "Paywall",
    rtlName: "Paywall",
    component: Dashboard,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/service-unavailable",
    name: "Service unavailable",
    rtlName: "Service unavailable",
    component: Dashboard,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/tradar",
    name: "Tradar",
    rtlName: "لوحة القيادة",
    icon: <RadioIcon color="inherit" />,
    component: Tables,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/trade-log",
    name: "Trade Log",
    rtlName: "لوحة القيادة",
    icon: <GalleryVerticalEndIcon color="inherit" />,
    component: TradeLog,
    layout: "",
  },
  {
    path: "/statistics",
    name: "Statistics",
    rtlName: "الإحصائيات",
    icon: <ChartSplineIcon color="inherit" />,
    component: Statistics,
    layout: "",
  },
  {
    path: "/reports",
    name: "Reports",
    rtlName: "التقارير",
    icon: <FileChartLineIcon color="inherit" />,
    component: Reports,
    layout: "",
  },
  {
    path: "/workspace",
    name: "Workspace",
    rtlName: "Workspace",
    icon: <LayoutPanelTopIcon size={22} />,
    component: WorkspaceHubPage,
    layout: "",
  },
  {
    path: "/workspace/strategy/:strategyId",
    name: "Strategy workspace",
    rtlName: "Strategy workspace",
    component: WorkspaceStrategyCanvasPage,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/workspace/:workspaceId",
    name: "Workspace canvas",
    rtlName: "Workspace canvas",
    component: WorkspaceCanvasPage,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/strategies",
    name: "Strategies",
    rtlName: "Strategies",
    icon: <LayersIcon color="inherit" />,
    component: Strategies,
    layout: "",
  },
  {
    path: "/strategies/:strategyId",
    name: "Strategy",
    rtlName: "Strategy",
    component: StrategyDetail,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/simulator/results",
    name: "Simulation results",
    rtlName: "Simulation results",
    component: SimulatorResultsPage,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/simulator/runs/:backtestId",
    name: "Simulation run",
    rtlName: "Simulation run",
    component: SimulatorSavedRunPage,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/simulator/new",
    name: "New simulation",
    rtlName: "New simulation",
    component: SimulatorWizardPage,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/simulator",
    name: "Simulator",
    rtlName: "Simulator",
    icon: <PlayIcon color="inherit" />,
    component: SimulatorListPage,
    layout: "",
  },
  {
    path: "/execution-rules-simulator/results",
    name: "Legacy simulation results",
    rtlName: "Legacy simulation results",
    component: LegacySimulatorRedirect,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/execution-rules-simulator/runs/:backtestId",
    name: "Legacy simulation run",
    rtlName: "Legacy simulation run",
    component: LegacySimulatorRedirect,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/execution-rules-simulator/new",
    name: "Legacy new simulation",
    rtlName: "Legacy new simulation",
    component: LegacySimulatorRedirect,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/execution-rules-simulator",
    name: "Legacy simulator",
    rtlName: "Legacy simulator",
    component: LegacySimulatorRedirect,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/backtests",
    name: "Simulations",
    rtlName: "Simulations",
    component: BacktestsListRedirect,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/reports/new",
    name: "Create report",
    rtlName: "Create report",
    component: GenerateReportPage,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/reports/:reportId",
    name: "Report Detail",
    rtlName: "تفاصيل التقرير",
    component: ReportDetail,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/backtests/:backtestId",
    name: "Simulation detail",
    rtlName: "تفاصيل الاختبار",
    component: BacktestDetail,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/university/course/:courseId/module/:moduleId/lesson/:lessonId",
    name: "Course Learn",
    rtlName: "تعلم الدورة",
    component: CourseLearn,
    layout: "",
  },
  {
    path: "/university/course/:courseId",
    name: "Course Overview",
    rtlName: "نظرة عامة على الدورة",
    component: CourseOverview,
    layout: "",
  },
  {
    path: "/university",
    name: "University",
    rtlName: "جامعة",
    icon: <BrainIcon color="inherit" />,
    component: University,
    layout: "",
  },
  {
    path: "/journal",
    name: "Journal",
    rtlName: "مجلة",
    icon: <JournalIcon color="inherit" />,
    component: Journal,
    layout: "",
  },
  {
    path: "/profile/brokers/ctrader/callback",
    name: "cTrader OAuth Callback",
    rtlName: "cTrader OAuth",
    component: CtraderOAuthCallback,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/billing/success",
    name: "Billing Success",
    rtlName: "نجاح الدفع",
    icon: <CreditIcon color="inherit" />,
    component: BillingSuccess,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/billing/canceled",
    name: "Billing Canceled",
    rtlName: "إلغاء الدفع",
    icon: <CreditIcon color="inherit" />,
    component: BillingCanceled,
    layout: "",
    hideFromSidebar: true,
  },
  {
    path: "/system",
    name: "System",
    rtlName: "النظام",
    icon: <SettingsMotionIcon color="inherit" />,
    component: Admin,
    layout: "",
  },
  {
    path: "/rtl-support-page",
    name: "RTL",
    rtlName: "آرتيإل",
    icon: <SupportIcon color="inherit" />,
    component: RTLPage,
    layout: "/rtl",
  },
  {
    name: "ACCOUNT PAGES",
    category: "account",
    rtlName: "صفحات",
    state: "pageCollapse",
    views: [
      {
        path: "/profile",
        name: "Account Settings",
        rtlName: "لوحة القيادة",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: ProfileTabRedirect,
        layout: "",
      },
      {
        path: "/profile/broker-api/connect",
        name: "Connect broker",
        rtlName: "Connect broker",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: ConnectBrokerWizardPage,
        layout: "",
        hideFromSidebar: true,
      },
      {
        path: "/profile/:tabId",
        name: "Account Settings",
        rtlName: "لوحة القيادة",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: Profile,
        layout: "",
      },
      {
        path: "/notifications",
        name: "Notifications",
        rtlName: "الإشعارات",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: NotificationsCentre,
        layout: "",
        hideFromSidebar: true,
      },
      {
        path: "/notifications/category/:category",
        name: "Notifications",
        rtlName: "الإشعارات",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: NotificationsCentre,
        layout: "",
        hideFromSidebar: true,
      },
      {
        path: "/notifications/category/:category/:notificationId",
        name: "Notifications",
        rtlName: "الإشعارات",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: NotificationsCentre,
        layout: "",
        hideFromSidebar: true,
      },
      {
        path: "/notifications/:notificationId",
        name: "Notifications",
        rtlName: "الإشعارات",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: NotificationsCentre,
        layout: "",
        hideFromSidebar: true,
      },
    ],
  },
];

export default dashRoutes;

